import React, { useState } from 'react';
import {
  Paper, Typography, Box, TextField, Button, Grid, Switch, FormControlLabel,
  Alert, Snackbar
} from '@mui/material';
import {
  Security, Delete, Notifications, Save
} from '@mui/icons-material';

const ProfileSettings = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    publicProfile: true,
    dataCollection: false
  });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const handleSettingChange = (setting) => (event) => {
    setSettings(prev => ({
      ...prev,
      [setting]: event.target.checked
    }));
  };

  const handleSaveSettings = async () => {
    try {
      // Mock API call - replace with actual endpoint
      await new Promise(resolve => setTimeout(resolve, 1000));
      setNotification({
        open: true,
        message: 'Settings saved successfully! ⚙️',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to save settings. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        // Mock API call - replace with actual endpoint
        await new Promise(resolve => setTimeout(resolve, 1000));
        setNotification({
          open: true,
          message: 'Account deletion request submitted.',
          severity: 'info'
        });
      } catch (error) {
        console.error('Account deletion failed:', error);
        setNotification({
          open: true,
          message: 'Failed to delete account. Please contact support.',
          severity: 'error'
        });
      }
    }
  };

  return (
    <>
      <Paper elevation={4} sx={{
        p: 4,
        borderRadius: 4,
        background: 'linear-gradient(135deg, #ffffff, #f5fbf7)',
        border: '2px solid #43a04715'
      }}>
        <Typography variant="h4" fontWeight={800} gutterBottom sx={{
          color: '#43a047',
          background: 'linear-gradient(135deg, #43a047, #66bb6a)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          ⚙️ Account Settings
        </Typography>

        {/* Privacy Settings */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={600} color="#1b5e20" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security /> Privacy & Notifications
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={handleSettingChange('emailNotifications')}
                    color="primary"
                  />
                }
                label="Email Notifications"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.pushNotifications}
                    onChange={handleSettingChange('pushNotifications')}
                    color="primary"
                  />
                }
                label="Push Notifications"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.publicProfile}
                    onChange={handleSettingChange('publicProfile')}
                    color="primary"
                  />
                }
                label="Public Profile"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.dataCollection}
                    onChange={handleSettingChange('dataCollection')}
                    color="primary"
                  />
                }
                label="Data Collection for Analytics"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSettings}
            sx={{
              background: 'linear-gradient(135deg, #43a047, #66bb6a)',
              '&:hover': {
                background: 'linear-gradient(135deg, #66bb6a, #43a047)'
              }
            }}
          >
            Save Settings
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDeleteAccount}
            sx={{
              borderColor: '#e53935',
              color: '#e53935',
              '&:hover': {
                backgroundColor: '#e5393510',
                borderColor: '#d32f2f'
              }
            }}
          >
            Delete Account
          </Button>
        </Box>
      </Paper>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ borderRadius: 3 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProfileSettings;
